import { toJpeg } from "html-to-image";
import { IMAGE_DIMENSION_LIMIT } from "@/plugins/cropping/cropping.constant.ts";
import { useI18n } from "vue-i18n";

export const RICH_TEXT_IMAGE_EXPORT_WIDTH = 800;

export function useRichTextImageExport() {
  const { t } = useI18n();

  const isRichTextEmpty = (container: HTMLElement | null): boolean => {
    if (!container) {
      return true;
    }

    // 1. Check for any "Visual Tag" (Your custom .mo classes, images, etc.)
    // This ensures that if there's a tag representing an image/formula, we DON'T treat it as empty.
    const hasVisualTags =
      container.querySelector(".mo, img, svg, iframe, table, .sls-algebra-kit") !== null;
    if (hasVisualTags) {
      return false;
    }

    // 2. Check for actual text content
    // We use textContent and remove whitespace + Zero Width Spaces (\u200B)
    const text = container.textContent || "";
    const hasActualText = text.replace(/\s/g, "").replace(/\u200B/g, "").length > 0;

    return !hasActualText;
  };

  const cleanupMediaElementsForImage = (container: HTMLElement): void => {
    const figures = container.querySelectorAll<HTMLElement>("figure");

    figures.forEach((figure) => {
      const isUnsupportedMedia = figure.classList.contains("html5");

      if (!isUnsupportedMedia) {
        return;
      }

      const mediaPreview = figure.querySelector(".media-preview");
      if (!mediaPreview) {
        return;
      }

      const emptyPlaceholder = document.createElement("div");
      emptyPlaceholder.className = "empty-placeholder info";

      const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      icon.setAttribute("width", "120");
      icon.setAttribute("height", "120");
      icon.setAttribute("viewBox", "0 0 120 120");
      icon.setAttribute("fill", "none");
      icon.innerHTML =
        '<path d="M60 110c27.614 0 50-22.386 50-50S87.614 10 60 10 10 32.386 10 60s22.386 50 50 50z" fill="#F3F5F8"/>' +
        '<rect x="31" y="31" width="58" height="58" rx="2.954" fill="#fff"/>' +
        '<path fill="#fff" stroke="#CED3D9" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="4 12" d="M31 31h58v58H31z"/>' +
        '<rect x="41.25" y="45" width="37.5" height="30" rx="2.92" fill="#CED3D9"/>' +
        '<path d="M41.25 69.7083C41.25 69.5264 41.2996 69.3479 41.3936 69.192L50.0054 54.9064C50.3522 54.3311 51.1562 54.2545 51.6054 54.754L60.7328 64.9038C61.1321 65.3479 61.8292 65.3452 62.2251 64.898L65.5996 61.086C65.9396 60.7018 66.5153 60.6373 66.9319 60.9368L78.3336 69.1309C78.595 69.3188 78.75 69.6211 78.75 69.943V72C78.75 73.6569 77.4069 75 75.75 75H44.25C42.5931 75 41.25 73.6569 41.25 72V69.7083Z" fill="#85919F"/>' +
        '<circle cx="70.625" cy="53.125" r="4.375" fill="#F3F5F8"/>';

      const label = document.createElement("span");
      label.className = "main";
      label.textContent = t("media_content_cannot_be_displayed_placeholder_label");

      emptyPlaceholder.appendChild(icon);
      emptyPlaceholder.appendChild(label);

      mediaPreview.replaceWith(emptyPlaceholder);
    });
  };

  const createOffscreenClone = async (targetContent: HTMLElement): Promise<HTMLElement> => {
    const cloneContainer = document.createElement("div");
    cloneContainer.setAttribute("aria-hidden", "true");
    Object.assign(cloneContainer.style, {
      width: `${RICH_TEXT_IMAGE_EXPORT_WIDTH}px`,
      boxSizing: "border-box",
      position: "fixed",
      top: "0",
      left: "-100000px",
      pointerEvents: "none",
    });

    const clone = targetContent.cloneNode(true) as HTMLElement;
    clone.style.width = "100%";
    clone.style.maxWidth = "none";
    clone.style.height = "auto";
    clone.style.maxHeight = "none";
    clone.style.overflow = "visible";
    clone.style.boxSizing = "border-box";

    // Hide only the tools and annotation UI, keep the attachment placeholders (icons, filenames)
    const elementsToHide = clone.querySelectorAll<HTMLElement>(
      ".tools, .annotation-toolbar, .image-annotation",
    );
    elementsToHide.forEach((element) => {
      element.style.display = "none";
    });

    cleanupMediaElementsForImage(clone);

    cloneContainer.appendChild(clone);
    document.body.appendChild(cloneContainer);

    // Wait for all images to load and decode in the clone
    const images = cloneContainer.querySelectorAll<HTMLImageElement>("img");
    await Promise.all(
      Array.from(images).map(async (image) => {
        try {
          image.loading = "eager";
          image.decoding = "sync";

          if (image.complete && image.naturalWidth > 0) {
            try {
              if (typeof image.decode === "function") {
                await image.decode();
              }
            } catch {
              // ignore decode errors
            }
            return;
          }

          await new Promise<void>((resolve) => {
            image.onload = async () => {
              try {
                if (typeof image.decode === "function") {
                  await image.decode();
                }
              } catch {
                // ignore decode errors
              }
              resolve();
            };
            image.onerror = () => resolve();
          });
        } catch {
          // never block export
        }
      }),
    );

    // Ensure at least two paint cycles before snapshot (Safari/iPad may decode before paint)
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });

    // Small delay for layout to settle
    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, 120);
    });

    return cloneContainer;
  };

  const exportRichTextToImage = async (targetContent: HTMLElement): Promise<string> => {
    let cloneContainer: HTMLElement | null = null;

    try {
      if (isRichTextEmpty(targetContent)) {
        return "";
      }
      // Create cloneContainer with cloned content using original width
      cloneContainer = await createOffscreenClone(targetContent);
      const clonedContent = cloneContainer.firstElementChild as HTMLElement;

      const pixelRatio = 2;
      const contentWidth = clonedContent.getBoundingClientRect().width;
      const contentHeight = clonedContent.getBoundingClientRect().height;

      if (
        contentWidth > IMAGE_DIMENSION_LIMIT.WIDTH ||
        contentHeight > IMAGE_DIMENSION_LIMIT.HEIGHT
      ) {
        throw new Error("Rich text export exceeds render limit");
      }

      let scaleRatio = 1;
      const maxWidth = IMAGE_DIMENSION_LIMIT.WIDTH / pixelRatio;
      const maxHeight = IMAGE_DIMENSION_LIMIT.HEIGHT / pixelRatio;

      if (contentWidth > maxWidth) {
        scaleRatio = Math.min(scaleRatio, maxWidth / contentWidth);
      }
      if (contentHeight > maxHeight) {
        scaleRatio = Math.min(scaleRatio, maxHeight / contentHeight);
      }
      // use toJpeg, dont use toBlob as it forces blob to be png
      const dataUrl = await toJpeg(clonedContent, {
        quality: 0.8,
        pixelRatio: pixelRatio,
        backgroundColor: "#fff",
        canvasWidth: contentWidth * scaleRatio,
        canvasHeight: contentHeight * scaleRatio,
        cacheBust: true,
      });

      const blob = await (await fetch(dataUrl)).blob();
      return URL.createObjectURL(blob);
    } finally {
      // Cleanup cloneContainer
      if (cloneContainer && document.body.contains(cloneContainer)) {
        document.body.removeChild(cloneContainer);
      }
    }
  };

  return {
    exportRichTextToImage,
  };
}
